import React, { useEffect, useState } from 'react';
import { useController } from '@rest-hooks/react';
import PropTypes from 'prop-types';

import OrderResource from 'resources/user/OrderResource';

import AmountModal from './AmountModal';
import PaymentModal from './PaymentModal';

import isMobileDevice from 'utils/mobileAndTabletCheck';
import { useError } from 'utils/useErrorController';
import { checkLoginStatus } from 'utils/checkLoginStatus';
import { calculateLineMaxAmount } from 'utils/calculateProductMaxAmount';
import RepeatPurchaseModal from './RepeatPurchaseModal';
import { createRecentOrderLookupParams } from './recentOrderLookupParams';

const createGAData = (product, quantity) => {
  const { discount_price, unit_price, net_discount, title, category, id, currency } = product;
  const itemPriceInCents = discount_price || unit_price;

  return {
    currency: currency,
    value: (itemPriceInCents / 100) * quantity,
    items: [{
      item_id: id?.toString(),
      item_name: title,
      item_category: category,
      price: itemPriceInCents / 100,
      quantity: quantity,
      ...(net_discount && { discount: net_discount / 100 })
    }]
  };
};

const createFBData = (product, quantity) => {
  const { discount_price, unit_price, title, category, id, currency } = product;
  const itemPriceInCents = discount_price || unit_price;

  return {
    content_ids: [id?.toString()],
    content_name: title,
    content_type: 'product',
    content_category: category,
    contents: [{
      id: id?.toString(),
      quantity: quantity,
    }],
    currency: currency,
    value: (itemPriceInCents / 100) * quantity
  };
};

const CheckoutModal = ({
  checkoutModals,
  checkoutVars,
  product,
  onAuthModalOpen,
  onCheckoutModalsChange,
  onCheckoutVarsChange,
  onClose,
  onProductChange,
  isCart,
 }) => {

  const [openRepeatPurchaseModal, setOpenRepeatPurchaseModal] = useState(false);

  const setStep = (step) => {
    onCheckoutVarsChange({ ...checkoutVars, step });
  };

  const { amountModal, paymentModal } = checkoutModals;
    
  const openPaymentModal = () => {
    setOpenRepeatPurchaseModal(false);
    onCheckoutModalsChange({ ...checkoutModals, amountModal: false, paymentModal: true });
  };

  const closeRepeatPurchaseModal = () => {
    setOpenRepeatPurchaseModal(false);
    onClose();
  };

  const { fetch } = useController();
  const { handleError } = useError();

  const handlePurchase = async () => {
    // Track add_to_cart when user proceeds with selected quantity
    if (window.trackEvent && product) {
      const ga_data = createGAData(product, checkoutVars.amount);
      const fb_data = createFBData(product, checkoutVars.amount);

      window.trackEvent('add_to_cart', ga_data, fb_data);
    }

    //check login status here and redirect to authModal if not logged in
    const isLoggedIn = checkLoginStatus();
    if (isLoggedIn) {
      try {
        const orders = await fetch(
          OrderResource.list(),
          createRecentOrderLookupParams(product)
        );
        if (orders.length > 0) {
          setOpenRepeatPurchaseModal(true);
        } else {
          openPaymentModal();
        }
      } catch (error) {
        handleError(error);
      }

    } else {
      onAuthModalOpen();
    }
  };

  const handlePurchaseRequest = () => {
    const newMaxAmount = calculateLineMaxAmount(product, product.selected_variant);

    onCheckoutVarsChange({ ...checkoutVars, maxAmount: newMaxAmount });
    onCheckoutModalsChange({ ...checkoutModals, amountModal: true });
  };
    
  useEffect(() => {
    handlePurchaseRequest();
  }, []);
    
  const isMobile = isMobileDevice();
  const showLargeModal = !isMobile && checkoutVars.step === 1;
  const modalWidth = showLargeModal ? "md" : "sm";

  return (
    <>
      <AmountModal
        open={ amountModal }
        onClose={ onClose }
        modalWidth={ modalWidth }
        object={ product }
        onObjectChange={ onProductChange }
        checkoutVars={ checkoutVars }
        onCheckoutVarsChange={ onCheckoutVarsChange }
        done={ handlePurchase } />

      <PaymentModal
        pricingId={ product?.pricing_id }
        object={ product }
        open={ paymentModal }
        step={ checkoutVars.step }
        promoCode={ checkoutVars.promoCode }
        setStep={ setStep }
        handleModalsClose={ onClose }
        stripe_purchase={ true }
        modalWidth={ modalWidth }
        amount={ checkoutVars.amount } />

      <RepeatPurchaseModal
        open={ openRepeatPurchaseModal }
        onConfirm={ openPaymentModal }
        onClose={ closeRepeatPurchaseModal }
        objectCategory={ product.category }
        objectRedeemedCount={ product.redeemed_count }
        isCart={ isCart } />
     
    </>
  );
};

CheckoutModal.propTypes = {
  checkoutModals: PropTypes.object,
  checkoutVars: PropTypes.object,
  product: PropTypes.object,
  onAuthModalOpen: PropTypes.func,
  onCheckoutModalsChange: PropTypes.func,
  onCheckoutVarsChange: PropTypes.func,
  onClose: PropTypes.func,
  onProductChange: PropTypes.func,
  isCart: PropTypes.bool,
};

export default CheckoutModal;
